<template>
  <a-drawer
    :visible="visible"
    :width="drawerWidth"
    title="메시지 발송"
    placement="right"
    @close="handleClose"
    class="message-send-drawer"
    :class="{ 'mobile-drawer': drawerWidth === '100%' }"
  >
    <!-- 수신자 필터링 (공통) -->
    <div class="recipients-filter-section">
      <h4 class="section-title">수신자 필터링</h4>
      
      <!-- 블랙리스트 필터 -->
      <div class="filter-controls blacklist-filter">
        <a-form-item label="블랙리스트 필터" style="margin-bottom: 16px;">
          <div class="blacklist-control">
            <a-switch 
              v-model:checked="blacklistFilterEnabled" 
              @change="handleBlacklistFilterToggle"
            >
              <template #checkedChildren>적용</template>
              <template #unCheckedChildren>미적용</template>
            </a-switch>
            <span class="filter-label">블랙리스트 고객 자동 제외</span>
            <a-button 
              v-if="blacklistFilterEnabled && !blacklistChecked" 
              size="small" 
              type="primary" 
              @click="checkBlacklist"
              :loading="checkingBlacklist"
            >
              블랙리스트 검사
            </a-button>
          </div>
        </a-form-item>
        
        <div v-if="blacklistFilterEnabled && blacklistChecked" class="blacklist-result">
          <a-alert
            :message="blacklistInfo.message"
            :type="blacklistInfo.type"
            show-icon
            closable
            @close="clearBlacklistFilter"
          >
            <template #description v-if="blacklistInfo.details">
              <div>대상 총 {{ blacklistInfo.totalCount }}명 중 {{ blacklistInfo.blacklistedCount }}명이 블랙리스트에 포함되어 제외됩니다.</div>
              <div v-if="blacklistInfo.blacklistedCount > 0" class="blacklist-numbers">
                <a-button size="small" type="link" @click="showBlacklistModal = true">
                  제외된 번호 보기
                </a-button>
              </div>
            </template>
          </a-alert>
        </div>
      </div>
      
      <!-- 병원 필터 -->
      <div class="filter-controls">
        <a-form-item label="제외할 병원 입력" style="margin-bottom: 12px;">
          <a-select
            v-model:value="excludedHospitals"
            mode="tags"
            placeholder="제외할 병원명을 입력하세요 (Enter로 추가)"
            style="width: 100%"
            @change="handleHospitalFilterChange"
          >
            <a-select-option v-for="hospital in availableHospitals" :key="hospital" :value="hospital">
              {{ hospital }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <div v-if="excludedHospitals.length > 0" class="excluded-info">
          <a-alert
            :message="`${excludedHospitals.length}개 병원의 고객 ${excludedRecipientsCount}명이 제외되었습니다`"
            type="warning"
            show-icon
            closable
            @close="clearHospitalFilter"
          />
        </div>
      </div>
    </div>

    <!-- 수신자 정보 (공통) -->
    <div class="recipients-section">
      <h4 class="section-title">수신자 정보</h4>
      <div class="recipients-info">
        <a-alert
          :message="`${filteredRecipients.length}명에게 메시지를 발송합니다`"
          :type="filteredRecipients.length === 0 ? 'warning' : 'info'"
          show-icon
        />
        <div class="recipients-list" v-if="filteredRecipients.length <= 5 && filteredRecipients.length > 0">
          <a-tag v-for="recipient in filteredRecipients" :key="recipient.id || recipient._id">
            {{ recipient.name }} ({{ recipient.phone }})
          </a-tag>
        </div>
        <div v-else-if="filteredRecipients.length > 5" class="recipients-summary">
          <a-space>
            <span>{{ filteredRecipients[0].name }} 외 {{ filteredRecipients.length - 1 }}명</span>
            <a-button size="small" @click="showRecipientsModal = true">
              전체 보기
            </a-button>
          </a-space>
        </div>
        <div v-else-if="filteredRecipients.length === 0" class="no-recipients">
          <a-empty description="발송 대상이 없습니다" />
        </div>
      </div>
    </div>

    <!-- 메시지 내용 섹션 (탭으로 구분) -->
    <div class="message-section">
      <h4 class="section-title">메시지 내용</h4>
      
      <!-- 메시지 타입 탭 -->
      <a-tabs v-model:activeKey="activeMessageTab">
        <a-tab-pane key="alimtalk" tab="알림톡">
          <!-- 알림톡 내용 -->
          <a-form
            :model="messageForm"
            :rules="rules"
            ref="formRef"
            layout="vertical"
          >
        <a-form-item label="템플릿 코드" name="templateCode">
          <div style="display: flex; gap: 8px; width: 100%;">
            <a-input 
              v-model:value="messageForm.templateCode" 
              placeholder="템플릿 코드를 입력하세요 (예: PROD_ZEISS_EVENT_WINNER_202501_V1)"
              style="flex: 1"
              :disabled="loadingTemplate"
              @press-enter="fetchTemplate"
            />
            <a-button 
              type="primary" 
              @click="fetchTemplate"
              :loading="loadingTemplate"
              style="flex-shrink: 0"
            >
              조회
            </a-button>
          </div>
          <div v-if="templateInfo" class="template-info">
            <!-- 템플릿 이미지가 있는 경우 표시 -->
            <div v-if="templateInfo.templateImageUrl || templateInfo.templateImageName" class="template-image-preview">
              <img 
                v-if="!imageLoadError"
                :src="getTemplateImageUrl()" 
                :alt="templateInfo.templateImageName || '템플릿 이미지'"
                @error="handleImageError"
                @load="imageLoadError = false"
                style="display: block; margin: 0 auto;"
              />
              <div v-if="imageLoadError" style="color: #999; padding: 20px; text-align: center;">
                이미지를 불러올 수 없습니다
              </div>
            </div>
            
            <a-descriptions :column="1" size="small" bordered class="mt-2">
              <a-descriptions-item label="템플릿 코드">{{ templateInfo.templateCode }}</a-descriptions-item>
              <a-descriptions-item label="템플릿명">{{ templateInfo.templateName }}</a-descriptions-item>
              <a-descriptions-item label="상태">
                <a-space>
                  <a-tag :color="getTemplateStatusColor(templateInfo.templateStatus)">
                    {{ getTemplateStatusText(templateInfo.templateStatus) }}
                  </a-tag>
                  <a-tag v-if="templateInfo.kepStatus" :color="templateInfo.kepStatus === 'O' ? 'green' : 'red'">
                    KEP: {{ templateInfo.kepStatus }}
                  </a-tag>
                </a-space>
              </a-descriptions-item>
              <a-descriptions-item label="메시지 유형">
                {{ getMessageTypeText(templateInfo.templateMessageType) }}
              </a-descriptions-item>
              <a-descriptions-item label="강조 유형" v-if="templateInfo.templateEmphasizeType">
                {{ templateInfo.templateEmphasizeType }}
              </a-descriptions-item>
              <a-descriptions-item label="카테고리 코드" v-if="templateInfo.categoryCode">
                {{ templateInfo.categoryCode }}
              </a-descriptions-item>
              <a-descriptions-item label="버튼" v-if="templateInfo.buttons && templateInfo.buttons.length > 0">
                <div v-for="button in templateInfo.buttons" :key="button.ordering" class="template-button-info">
                  <a-tag color="blue">
                    {{ button.ordering }}. {{ button.name }} ({{ button.linkType }})
                  </a-tag>
                </div>
              </a-descriptions-item>
            </a-descriptions>
          </div>
          <a-alert 
            v-if="templateError" 
            :message="templateError" 
            type="error" 
            class="mt-2"
            closable
            @close="templateError = null"
          />
        </a-form-item>

            <a-form-item label="발송 시간" name="sendTime">
              <a-radio-group v-model:value="messageForm.sendTime">
                <a-radio value="now">즉시 발송</a-radio>
                <a-radio value="scheduled" disabled>예약 발송 (준비중)</a-radio>
              </a-radio-group>
            </a-form-item>
          </a-form>

          <!-- 미리보기 -->
          <div class="preview-section" v-if="templateInfo">
            <h4 class="section-title">메시지 미리보기</h4>
            <div class="preview-container">
              <div class="kakao-preview">
                <div class="kakao-header">
                  <span style="font-size: 13px; font-weight: 600;">서티라이프</span>
                </div>
                <!-- 이미지형 템플릿인 경우 이미지 표시 -->
                <div v-if="templateInfo && (templateInfo.templateImageUrl || templateInfo.templateImageName)" class="kakao-image">
                  <img 
                    v-if="!previewImageError"
                    :src="getTemplateImageUrl()" 
                    :alt="templateInfo.templateImageName || '템플릿 이미지'"
                    @error="handlePreviewImageError"
                    style="width: 100%; display: block;"
                  />
                  <div v-if="previewImageError" style="padding: 40px; text-align: center; color: #999; background: #f5f5f5;">
                    이미지를 불러올 수 없습니다
                  </div>
                </div>
                <div class="kakao-title" v-if="templateInfo.templateName">{{ templateInfo.templateName }}</div>
                <div class="kakao-content" v-if="templateInfo.templateContent">{{ templateInfo.templateContent }}</div>
                <!-- 템플릿에 버튼이 있는 경우 버튼 표시 -->
                <div v-if="templateInfo && templateInfo.buttons && templateInfo.buttons.length > 0" class="kakao-buttons">
                  <div v-for="button in templateInfo.buttons" :key="button.ordering" class="kakao-button">
                    {{ button.name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </a-tab-pane>
        
        <a-tab-pane key="brandtalk" tab="브랜드톡">
          <div class="brandtalk-content">
            <!-- 브랜드 메시지 타입 선택 -->
            <div class="brandtalk-type-section">
              <label style="display: block; margin-bottom: 12px; font-weight: 500;">
                브랜드 메시지 타입 <span style="color: #ff4d4f;">*</span>
                <a-tooltip placement="right" overlay-class-name="brandtalk-tooltip">
                  <template #title>
                    <div style="width: 100%; max-height: 70vh; overflow-y: auto;">
                      <!-- 1. 브랜드 메시지 이해 -->
                      <div style="margin-bottom: 16px;">
                        <div style="font-weight: 600; color: #1890ff; margin-bottom: 8px; border-bottom: 1px solid #e8e8e8; padding-bottom: 4px;">1. 브랜드 메시지 이해</div>
                        <p style="margin: 0; color: #333;">브랜드메시지(구. 친구톡 업그레이드)는 기존 친구톡의 업그레이드된 버전입니다.</p>
                        <p style="margin: 4px 0 0; color: #ff4d4f;">※ 기존 친구톡은 2025.12.31 서비스 종료 예정이며, 브랜드메시지 서비스로 전환될 예정입니다.</p>
                        <p style="margin: 8px 0 0; color: #333;">광고주가 마케팅수신동의자(이하. 마수동)에게 발송을 위해서는 발신프로필 단위로 발송 권한을 획득해야 하며, 무료수신거부 전화번호가 등록되어야 합니다.</p>
                        <p style="margin: 8px 0 0; color: #333;">브랜드 메시지에는 크게 두 가지 기능이 추가되었습니다:</p>

                        <div style="margin-top: 8px; padding: 10px; background: #f6ffed; border-radius: 4px; color: #333; border: 1px solid #b7eb8f;">
                          <strong style="color: #389e0d;">1. 기본형 (템플릿 등록 후 발송하는 형태)</strong>
                          <ul style="margin: 6px 0 0; padding-left: 20px; color: #333; font-size: 12px;">
                            <li>기존 알림톡처럼 템플릿을 등록 후 발송하는 형태입니다.</li>
                            <li>알림톡 템플릿은 검수 승인 후 발송할 수 있으나, <strong>브랜드 메시지 템플릿은 승인 절차가 없습니다.</strong></li>
                            <li>따라서, 브랜드 메시지 템플릿 등록 이후 <strong>즉시 발송</strong>할 수 있습니다.</li>
                            <li style="color: #ff4d4f;">알림톡 템플릿으로 브랜드 메시지를 발송할 수 없습니다. (반대의 경우도 미지원)</li>
                          </ul>
                        </div>

                        <div style="margin-top: 8px; padding: 10px; background: #e6f7ff; border-radius: 4px; color: #333; border: 1px solid #91d5ff;">
                          <strong style="color: #096dd9;">2. Targeting (타게팅)</strong>
                          <ul style="margin: 6px 0 0; padding-left: 20px; color: #333; font-size: 12px;">
                            <li>기본형(템플릿 등록 후 발송) 타입만 지원합니다.</li>
                            <li>마수동을 받은 경우 <strong>채널 미추가 사용자에게도 발송</strong>할 수 있습니다.</li>
                          </ul>
                        </div>
                      </div>

                      <!-- 2. 브랜드 메시지 Targeting -->
                      <div style="margin-bottom: 16px;">
                        <div style="font-weight: 600; color: #1890ff; margin-bottom: 8px; border-bottom: 1px solid #e8e8e8; padding-bottom: 4px;">2. 브랜드 메시지 Targeting (타게팅)</div>
                        <ul style="margin: 0; padding-left: 20px; color: #333; font-size: 13px;">
                          <li>Targeting은 <strong>기본형(템플릿 등록 후 발송) 타입만 지원</strong></li>
                          <li>기존 친구톡은 채널추가된 친구에게만 발송할 수 있었습니다. 수신자가 채널 미추가시 <strong style="color: #ff4d4f;">3018(NoSendAvailableException)</strong> 실패됩니다.</li>
                          <li>브랜드 메시지는 광고주가 마수동을 받은 경우 <strong>채널 추가하지 않은 사용자에게도 발송</strong>할 수 있습니다.</li>
                          <li><strong>Targeting은 M, N, I, F로 구분됩니다.</strong> (기존 친구톡은 'I'와 동일)</li>
                        </ul>

                        <img
                          :src="brandtalkApiCompatibilityImage"
                          alt="브랜드 메시지 Targeting 안내"
                          style="margin-top: 12px; max-width: 100%; border-radius: 4px; border: 1px solid #d9d9d9;"
                        />
                        <p style="margin: 4px 0 0; font-size: 11px; color: #8c8c8c;">이미지 출처: 카카오 비즈메시지 API 가이드문서</p>

                        <div style="margin-top: 12px; padding: 10px; background: #fff7e6; border-radius: 4px; font-size: 12px; color: #333; border: 1px solid #ffd591;">
                          <strong style="color: #d46b08;">브랜드메시지 타입별 발송 조건</strong>
                          <div style="margin-top: 8px;">
                            <div style="margin-bottom: 8px; padding: 8px; background: #fff; border-radius: 4px; border: 1px solid #ffd591;">
                              <strong style="color: #d46b08;">M / N (마수동 회원 대상 발송)</strong>
                              <ol style="margin: 4px 0 0; padding-left: 20px; font-size: 11px;">
                                <li>비즈니스 인증 채널</li>
                                <li>사업자 번호 등록 (채널관리자센터 > 관리 > 비즈니스 채널 신청 > 사업자정보 등록)</li>
                                <li>채널 고객센터 전화번호 등록</li>
                                <li>채널 친구수 5만 이상</li>
                                <li>3개월 내 알림톡 발송 성공 이력 보유</li>
                                <li style="color: #8c8c8c;">채널에 080 무료수신거부 전화번호 등록 (API 제공 안내 예정)</li>
                                <li style="color: #8c8c8c;">광고주(고객사) 마케팅 수신 동의 화면 증적 파일 (API 제공 안내 예정)</li>
                              </ol>
                            </div>
                            <div style="display: flex; gap: 8px;">
                              <div style="flex: 1; padding: 6px 8px; background: #fff; border-radius: 4px; border: 1px solid #ffd591;">
                                <strong style="color: #d46b08;">I</strong>: 비즈니스 인증 필요 - 채널 친구
                              </div>
                              <div style="flex: 1; padding: 6px 8px; background: #fff; border-radius: 4px; border: 1px solid #ffd591;">
                                <strong style="color: #d46b08;">F</strong>: 채널 친구 대상
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- 3. 브랜드메시지 지원 타입 -->
                      <div style="margin-bottom: 16px;">
                        <div style="font-weight: 600; color: #1890ff; margin-bottom: 8px; border-bottom: 1px solid #e8e8e8; padding-bottom: 4px;">3. 브랜드메시지 지원 타입</div>
                        <!-- 헤더 -->
                        <div style="display: grid; grid-template-columns: 70px 1fr 100px; gap: 2px; font-size: 12px; margin-bottom: 4px;">
                          <div style="padding: 4px 8px; background: #1890ff; color: #fff; font-weight: 600; text-align: center; border-radius: 2px 0 0 0;">구분</div>
                          <div style="padding: 4px 8px; background: #1890ff; color: #fff; font-weight: 600; text-align: center;">설명</div>
                          <div style="padding: 4px 8px; background: #1890ff; color: #fff; font-weight: 600; text-align: center; border-radius: 0 2px 0 0;">마수동 발송</div>
                        </div>
                        <!-- 데이터 행 -->
                        <div style="display: grid; grid-template-columns: 70px 1fr 100px; gap: 2px; font-size: 12px; color: #333;">
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center;"><strong style="color: #1890ff;">U_FT</strong></div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9;">브랜드 메시지 텍스트</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center; color: #52c41a; font-weight: 600;">O</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center;"><strong style="color: #1890ff;">U_FI</strong></div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9;">브랜드 메시지 이미지</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center; color: #52c41a; font-weight: 600;">O</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center;"><strong style="color: #1890ff;">U_FW</strong></div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9;">브랜드 메시지 와이드 이미지</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center; color: #52c41a; font-weight: 600;">O</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center;"><strong style="color: #1890ff;">U_FA</strong></div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9;">브랜드 메시지 캐러셀 커머스</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center; color: #52c41a; font-weight: 600;">O</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center;"><strong style="color: #1890ff;">U_FP</strong></div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9;">브랜드 메시지 프리미엄 동영상</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center; color: #52c41a; font-weight: 600;">O</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center;"><strong style="color: #1890ff;">U_FC</strong></div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9;">브랜드 메시지 캐러셀 피드</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center; color: #52c41a; font-weight: 600;">O</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center;"><strong style="color: #1890ff;">U_FL</strong></div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9;">브랜드 메시지 와이드 리스트</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center; color: #52c41a; font-weight: 600;">O</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center;"><strong style="color: #1890ff;">U_FM</strong></div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9;">브랜드 메시지 커머스</div>
                          <div style="padding: 4px 8px; background: #f5f5f5; border: 1px solid #d9d9d9; text-align: center; color: #52c41a; font-weight: 600;">O</div>
                        </div>
                      </div>

                      <!-- 4. UPGRADE_TP 파라미터 설명 -->
                      <div>
                        <div style="font-weight: 600; color: #1890ff; margin-bottom: 8px; border-bottom: 1px solid #e8e8e8; padding-bottom: 4px;">4. UPGRADE_TP 파라미터</div>
                        <div style="font-size: 12px; color: #333;">
                          <!-- BN: 변수 미사용 (기본형) -->
                          <div style="padding: 8px 10px; background: #e6f7ff; border-radius: 4px; margin-bottom: 6px; border: 1px solid #91d5ff;">
                            <strong style="color: #096dd9;">BN (변수 미사용)</strong> - 기본형<br/>
                            <span style="color: #333;">브랜드 메시지 템플릿에 변수부가 없을 경우 사용</span>
                          </div>

                          <!-- BV: 변수 분리형 (기본형) -->
                          <div style="padding: 8px 10px; background: #e6f7ff; border-radius: 4px; margin-bottom: 6px; border: 1px solid #91d5ff;">
                            <strong style="color: #096dd9;">BV (변수 분리형)</strong> - 기본형<br/>
                            <span style="color: #333;">변수부를 [_variable] 파라미터에 Map(Key:value) 형태로 요청</span>
                            <ul style="margin: 6px 0 0; padding-left: 18px; color: #595959; font-size: 11px;">
                              <li>message_variable: 메시지 본문 변수</li>
                              <li>button_variable: 버튼 변수</li>
                              <li>commerce_variable: 커머스 변수</li>
                              <li>coupon_variable: 쿠폰 변수</li>
                              <li>video_variable: 비디오 변수</li>
                              <li>carousel_variable: 캐러셀 변수</li>
                              <li>image_variable: 이미지 변수</li>
                            </ul>
                            <div style="margin-top: 6px; padding: 4px 8px; background: #fff; border-radius: 2px; font-size: 11px; color: #595959;">
                              ※ 변수가 있을 경우에만 파라미터 요청<br/>
                              ※ 변수 없을 경우 해당 파라미터 미작성
                            </div>
                          </div>

                          <!-- BM: 전문형 (기본형) -->
                          <div style="padding: 8px 10px; background: #e6f7ff; border-radius: 4px; margin-bottom: 6px; border: 1px solid #91d5ff;">
                            <strong style="color: #096dd9;">BM (전문형)</strong> - 기본형<br/>
                            <span style="color: #333;">알림톡 발송 방식과 동일</span><br/>
                            <span style="color: #595959; font-size: 11px;">변수가 있는 파라미터를 치환변수와 내용으로 전문 작성하여 발송 (알림톡처럼 사전에 템플릿 등록하는 방식)</span>
                          </div>

                          <!-- FY: 친구톡 호환 자유형 -->
                          <div style="padding: 8px 10px; background: #fffbe6; border: 1px solid #ffe58f; border-radius: 4px; margin-bottom: 6px;">
                            <strong style="color: #d48806;">FY (친구톡 호환)</strong> - 자유형<br/>
                            <span style="color: #333;">(구) 친구톡 요청/응답 양식 사용 호환</span><br/>
                            <span style="color: #d48806; font-weight: 600;">※ 2027년 지원 종료 예정</span>
                          </div>

                          <!-- FN: 친구톡 비호환 -->
                          <div style="padding: 8px 10px; background: #e6f7ff; border-radius: 4px; border: 1px solid #91d5ff;">
                            <strong style="color: #096dd9;">FN (친구톡 비호환)</strong><br/>
                            <span style="color: #333;">브랜드메시지 요청/응답 양식 사용</span>
                          </div>
                        </div>
                      </div>

                      <!-- 5. 파라미터 관계 분석 -->
                      <div style="margin-top: 16px; padding-top: 12px; border-top: 2px solid #1890ff;">
                        <div style="font-weight: 600; color: #1890ff; margin-bottom: 8px; border-bottom: 1px solid #e8e8e8; padding-bottom: 4px;">5. 파라미터 관계 분석 (messageType vs upgradeType)</div>

                        <!-- 핵심 차이 -->
                        <div style="margin-bottom: 10px; padding: 8px; background: #f0f5ff; border-radius: 4px; border: 1px solid #adc6ff;">
                          <div style="font-weight: 600; color: #2f54eb; margin-bottom: 6px;">핵심 차이</div>
                          <div style="display: grid; grid-template-columns: 100px 1fr; gap: 4px; font-size: 12px;">
                            <div style="padding: 4px 6px; background: #fff; border-radius: 2px;"><strong style="color: #1890ff;">messageType</strong></div>
                            <div style="padding: 4px 6px; background: #fff; border-radius: 2px; color: #333;">메시지 <strong>레이아웃</strong> - "어떤 모양으로 보여줄 것인가?"</div>
                            <div style="padding: 4px 6px; background: #fff; border-radius: 2px;"><strong style="color: #722ed1;">upgradeType</strong></div>
                            <div style="padding: 4px 6px; background: #fff; border-radius: 2px; color: #333;">API <strong>호출 방식</strong> - "어떤 방식으로 요청할 것인가?"</div>
                          </div>
                        </div>

                        <!-- 조합 예시 -->
                        <div style="margin-bottom: 10px;">
                          <div style="font-weight: 600; color: #333; margin-bottom: 6px; font-size: 12px;">조합 예시 (두 파라미터는 독립적으로 조합 가능)</div>
                          <div style="display: grid; grid-template-columns: 70px 70px 1fr; gap: 2px; font-size: 11px;">
                            <div style="padding: 4px 6px; background: #1890ff; color: #fff; text-align: center; font-weight: 600;">messageType</div>
                            <div style="padding: 4px 6px; background: #722ed1; color: #fff; text-align: center; font-weight: 600;">upgradeType</div>
                            <div style="padding: 4px 6px; background: #595959; color: #fff; font-weight: 600;">설명</div>

                            <div style="padding: 4px 6px; background: #e6f7ff; text-align: center; color: #1890ff; font-weight: 600;">U_FT</div>
                            <div style="padding: 4px 6px; background: #f9f0ff; text-align: center; color: #722ed1; font-weight: 600;">FY</div>
                            <div style="padding: 4px 6px; background: #fafafa; color: #333;">친구톡 호환 방식으로 텍스트 발송</div>

                            <div style="padding: 4px 6px; background: #e6f7ff; text-align: center; color: #1890ff; font-weight: 600;">U_FI</div>
                            <div style="padding: 4px 6px; background: #f9f0ff; text-align: center; color: #722ed1; font-weight: 600;">FY</div>
                            <div style="padding: 4px 6px; background: #fafafa; color: #333;">친구톡 호환 방식으로 이미지 발송</div>

                            <div style="padding: 4px 6px; background: #e6f7ff; text-align: center; color: #1890ff; font-weight: 600;">U_FT</div>
                            <div style="padding: 4px 6px; background: #f9f0ff; text-align: center; color: #722ed1; font-weight: 600;">BN</div>
                            <div style="padding: 4px 6px; background: #fafafa; color: #333;">템플릿 기반 텍스트 발송 (변수 없음)</div>

                            <div style="padding: 4px 6px; background: #e6f7ff; text-align: center; color: #1890ff; font-weight: 600;">U_FI</div>
                            <div style="padding: 4px 6px; background: #f9f0ff; text-align: center; color: #722ed1; font-weight: 600;">BV</div>
                            <div style="padding: 4px 6px; background: #fafafa; color: #333;">템플릿 + 변수 + 채널 미추가자까지 발송</div>
                          </div>
                        </div>

                        <!-- 결론 -->
                        <div style="padding: 8px; background: #f6ffed; border-radius: 4px; border: 1px solid #b7eb8f; font-size: 12px;">
                          <div style="font-weight: 600; color: #389e0d; margin-bottom: 4px;">결론</div>
                          <ul style="margin: 0; padding-left: 16px; color: #333;">
                            <li><strong style="color: #1890ff;">messageType</strong> = 메시지가 "어떻게 생겼나" (외형)</li>
                            <li><strong style="color: #722ed1;">upgradeType</strong> = API를 "어떻게 호출하나" (방식)</li>
                          </ul>
                          <div style="margin-top: 6px; padding-top: 6px; border-top: 1px dashed #b7eb8f;">
                            <strong style="color: #d48806;">FY</strong>: 기존 친구톡 API 그대로 쓰면서 브랜드메시지로 업그레이드<br/>
                            <strong style="color: #096dd9;">BN/BV/BM</strong>: 새 방식으로 템플릿 등록 후 사용 + <strong>Targeting 가능</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                  <span style="cursor: help; color: #1890ff; margin-left: 4px;">
                    <i class="fa fa-question-circle"></i>
                  </span>
                </a-tooltip>
              </label>

              <a-radio-group v-model:value="brandtalkForm.messageType" button-style="solid" style="width: 100%;">
                <a-radio-button value="U_FI" style="width: 50%; text-align: center;">
                  이미지형 (500x250)
                </a-radio-button>
                <a-radio-button value="U_FW" style="width: 50%; text-align: center;">
                  와이드형 (800x600)
                </a-radio-button>
              </a-radio-group>

              <!-- 타입별 설명 -->
              <div class="type-description" style="margin-top: 12px; padding: 12px; background: #f6ffed; border: 1px solid #b7eb8f; border-radius: 4px;">
                <div v-if="brandtalkForm.messageType === 'U_FI'" style="display: flex; align-items: flex-start;">
                  <i class="fa fa-info-circle" style="color: #52c41a; margin-right: 8px; margin-top: 2px;"></i>
                  <div>
                    <strong>이미지형 (U_FI)</strong>
                    <p style="margin: 4px 0 0; color: #595959; font-size: 13px;">
                      일반 크기 이미지(500x250px)를 포함하는 브랜드 메시지입니다.
                      <br/><span style="color: #ff4d4f;">이미지 업로드가 필수입니다.</span>
                    </p>
                  </div>
                </div>
                <div v-else-if="brandtalkForm.messageType === 'U_FW'" style="display: flex; align-items: flex-start;">
                  <i class="fa fa-info-circle" style="color: #52c41a; margin-right: 8px; margin-top: 2px;"></i>
                  <div>
                    <strong>와이드형 (U_FW)</strong>
                    <p style="margin: 4px 0 0; color: #595959; font-size: 13px;">
                      와이드 크기 이미지(800x600px)를 포함하는 브랜드 메시지입니다.
                      <br/><span style="color: #ff4d4f;">이미지 업로드가 필수입니다.</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 이미지 설정 (항상 표시, 이미지 필수) -->
            <div style="margin-top: 24px;">
              <label style="display: block; margin-bottom: 8px; font-weight: 500;">
                이미지 설정 <span style="color: #ff4d4f;">*</span>
                <a-tooltip>
                  <template #title>
                    <div>
                      <p>이미지형: 500x250px</p>
                      <p>와이드형: 800x600px</p>
                    </div>
                  </template>
                  <span style="cursor: help; color: #1890ff; margin-left: 4px;">
                    <i class="fa fa-question-circle"></i>
                  </span>
                </a-tooltip>
              </label>

              <div style="display: flex; gap: 8px; align-items: center;">
                <a-upload
                  :before-upload="beforeUploadBrandImage"
                  :show-upload-list="false"
                  accept="image/*"
                >
                  <a-button :loading="uploadingImage">
                    <i class="fa fa-upload" style="margin-right: 8px;"></i>
                    이미지 파일 선택
                  </a-button>
                </a-upload>
                <a-button @click="showResourceModal = true">
                  <template #icon><i class="fa fa-folder-open"></i></template>
                  자료실에서 선택
                </a-button>
              </div>
              <div style="margin-top: 8px; font-size: 12px; color: #666;">
                JPG, PNG 파일만 가능 (최대 2MB)
              </div>

              <!-- 업로드된 이미지 표시 -->
              <div v-if="brandtalkForm.imageUrl" class="uploaded-image-preview" style="margin-top: 16px;">
                <div style="position: relative; display: inline-block; width: 100%;">
                  <img
                    :src="brandtalkForm.imageUrl"
                    alt="업로드된 이미지"
                    style="max-width: 100%; max-height: 200px; border: 1px solid #d9d9d9; border-radius: 4px;"
                  />
                  <a-button
                    type="text"
                    danger
                    size="small"
                    @click="removeBrandtalkImage"
                    style="position: absolute; top: 5px; right: 5px; background: white;"
                  >
                    <i class="fa fa-times"></i>
                  </a-button>
                </div>
                <div style="margin-top: 8px;">
                  <div style="font-size: 12px; color: #666;">
                    {{ brandtalkForm.messageType === 'U_FW' ? '와이드 이미지 (800x600)' : '일반 이미지 (500x250)' }}
                  </div>
                </div>
              </div>
            </div>

            <!-- 메시지 작성 영역 -->
            <div style="margin-top: 24px;">
              <label style="display: block; margin-bottom: 8px; font-weight: 500;">
                메시지 내용 <span style="color: #ff4d4f;">*</span>
                <a-tooltip>
                  <template #title>수신자에게 전달될 브랜드 메시지 본문입니다. 최대 1000자까지 입력 가능합니다.</template>
                  <span style="cursor: help; color: #1890ff; margin-left: 4px;">
                    <i class="fa fa-question-circle"></i>
                  </span>
                </a-tooltip>
              </label>
              <a-textarea
                v-model:value="brandtalkForm.content"
                placeholder="브랜드 메시지 내용을 작성하세요..."
                :rows="6"
                style="width: 100%"
                :maxlength="1000"
                show-count
              />
            </div>

            <!-- 버튼 설정 -->
            <div style="margin-top: 24px;">
              <label style="display: block; margin-bottom: 8px; font-weight: 500;">
                버튼 설정 <span style="color: #8c8c8c; font-weight: normal;">(선택)</span>
                <a-tooltip>
                  <template #title>메시지 하단에 클릭 가능한 버튼을 추가합니다. 웹링크로 이동하는 버튼입니다.</template>
                  <span style="cursor: help; color: #1890ff; margin-left: 4px;">
                    <i class="fa fa-question-circle"></i>
                  </span>
                </a-tooltip>
              </label>
              <a-row :gutter="16">
                <a-col :span="12">
                  <a-input
                    v-model:value="brandtalkForm.buttonName"
                    placeholder="버튼명 입력 (예: 자세히 보기)"
                    :maxlength="14"
                  />
                  <div style="margin-top: 4px; font-size: 12px; color: #666;">
                    최대 14자
                  </div>
                </a-col>
                <a-col :span="12">
                  <a-input
                    v-model:value="brandtalkForm.buttonLink"
                    placeholder="링크 URL (https://...)"
                  />
                  <div style="margin-top: 4px; font-size: 12px; color: #666;">
                    버튼 클릭 시 이동할 URL
                  </div>
                </a-col>
              </a-row>
            </div>

            <!-- 대체 발송 설정 -->
            <div style="margin-top: 24px;">
              <label style="display: block; margin-bottom: 8px; font-weight: 500;">
                대체 발송 설정
                <a-tooltip>
                  <template #title>
                    <div>
                      <p>브랜드톡 발송 실패 시 SMS로 대체 발송합니다.</p>
                      <p style="margin-top: 4px;">발송 실패 케이스: 채널 친구가 아닌 경우, 수신 거부 설정 등</p>
                    </div>
                  </template>
                  <span style="cursor: help; color: #1890ff; margin-left: 4px;">
                    <i class="fa fa-question-circle"></i>
                  </span>
                </a-tooltip>
              </label>
              <div style="display: flex; align-items: center; gap: 12px;">
                <a-switch v-model:checked="brandtalkForm.fallbackEnabled">
                  <template #checkedChildren>적용</template>
                  <template #unCheckedChildren>미적용</template>
                </a-switch>
                <span style="color: #595959; font-size: 13px;">브랜드톡 발송 실패 시 SMS로 대체 발송</span>
              </div>
            </div>

            <!-- 발송 시간 설정 -->
            <div style="margin-top: 24px;">
              <label style="display: block; margin-bottom: 8px; font-weight: 500;">
                발송 시간
                <a-tooltip>
                  <template #title>
                    <div>
                      <p>야간 시간대(21:00~08:00)에는 광고성 메시지 발송이 제한됩니다.</p>
                    </div>
                  </template>
                  <span style="cursor: help; color: #1890ff; margin-left: 4px;">
                    <i class="fa fa-question-circle"></i>
                  </span>
                </a-tooltip>
              </label>
              <a-radio-group v-model:value="brandtalkForm.sendTime">
                <a-radio value="now">즉시 발송</a-radio>
                <a-radio value="scheduled" disabled>예약 발송 (준비중)</a-radio>
              </a-radio-group>
              <div style="margin-top: 8px; font-size: 12px; color: #fa8c16;">
                <i class="fa fa-exclamation-triangle" style="margin-right: 4px;"></i>
                야간 시간대(21:00~08:00)에는 발송이 제한됩니다.
              </div>
            </div>

            <!-- 미리보기 -->
            <div style="margin-top: 24px;">
              <label style="display: block; margin-bottom: 8px; font-weight: 500;">미리보기</label>
              <div class="preview-container">
                <div class="kakao-preview" style="background: #9BBBD4; padding: 0; border-radius: 8px; overflow: hidden;">
                  <!-- 헤더 -->
                  <div style="padding: 12px 16px; background: #9BBBD4; display: flex; align-items: center;">
                    <div style="width: 24px; height: 24px; border-radius: 6px; background: #FEE500; display: flex; align-items: center; justify-content: center; margin-right: 8px;">
                      <span style="font-size: 10px; font-weight: bold;">브랜드</span>
                    </div>
                    <span style="font-size: 14px; color: #000;">서티라이프 certilife</span>
                  </div>

                  <!-- 메시지 콘텐츠 영역 -->
                  <div style="background: white; margin: 0 8px 8px 8px; border-radius: 8px; overflow: hidden;">
                    <!-- 이미지 (이미지가 있을 때 표시) -->
                    <div v-if="brandtalkForm.imageUrl" style="width: 100%;">
                      <img
                        :src="brandtalkForm.imageUrl"
                        alt="브랜드톡 이미지"
                        style="width: 100%; display: block;"
                      />
                    </div>

                    <!-- 텍스트 내용 -->
                    <div style="padding: 16px; white-space: pre-wrap; font-size: 14px; line-height: 1.5; color: #000;">{{ brandtalkForm.content || '메시지를 입력하세요...' }}</div>

                    <!-- 버튼 (버튼명이 있을 때 표시) -->
                    <div v-if="brandtalkForm.buttonName" style="padding: 0 16px 16px;">
                      <div style="background: #f7f7f7; border-radius: 6px; padding: 12px; text-align: center; font-size: 14px; color: #000; cursor: pointer;">
                        {{ brandtalkForm.buttonName }}
                      </div>
                    </div>
                  </div>

                  <!-- 하단 시간 표시 -->
                  <div style="padding: 0 16px 12px; font-size: 11px; color: #495057; text-align: right;">
                    오후 3:39
                  </div>
                </div>
              </div>
            </div>
          </div>
        </a-tab-pane>
      </a-tabs>
    </div>

    <!-- 하단 액션 버튼 -->
    <template #footer>
      <a-space>
        <a-button @click="handleClose">취소</a-button>
        <a-button
          type="primary"
          @click="handleSend"
          :loading="sending"
          :disabled="isSendButtonDisabled"
          class="yellow-button"
        >
          {{
            sending
              ? '발송 중...'
              : `${filteredRecipients.length}명에게 ${activeMessageTab === 'brandtalk' ? '브랜드톡' : '알림톡'} 발송`
          }}
        </a-button>
      </a-space>
    </template>

    <!-- 전체 수신자 목록 모달 -->
    <a-modal
      v-model:visible="showRecipientsModal"
      title="전체 수신자 목록"
      :footer="null"
      width="500px"
    >
      <div v-if="excludedHospitals.length > 0" class="filter-info">
        <a-alert
          :message="`필터링 적용: ${excludedHospitals.join(', ')} 병원 고객 제외`"
          type="warning"
          style="margin-bottom: 16px;"
        />
      </div>
      <a-list
        :data-source="filteredRecipients"
        size="small"
      >
        <template #renderItem="{ item }">
          <a-list-item>
            <a-list-item-meta>
              <template #title>
                {{ item.name }}
              </template>
              <template #description>
                {{ item.phone }} | {{ item.hospital || '-' }}
              </template>
            </a-list-item-meta>
          </a-list-item>
        </template>
      </a-list>
    </a-modal>
    <!-- 블랙리스트 번호 목록 모달 -->
    <a-modal
      v-model:visible="showBlacklistModal"
      title="블랙리스트 제외 번호"
      :footer="null"
      width="500px"
    >
      <div class="blacklist-modal-content">
        <a-alert
          :message="`총 ${blacklistInfo.blacklistedCount}개의 번호가 블랙리스트에 포함되어 제외되었습니다.`"
          type="warning"
          style="margin-bottom: 16px;"
        />
        <div class="blacklist-numbers-list">
          <a-tag v-for="phone in blacklistInfo.blacklistedPhoneNumbers" :key="phone" color="red">
            {{ phone }}
          </a-tag>
        </div>
      </div>
    </a-modal>

    <!-- 자료실 모달 -->
    <EventResourceModal
      :open="showResourceModal"
      :api="api"
      @select="handleResourceFileSelect"
      @cancel="showResourceModal = false"
    />
  </a-drawer>
</template>

<script>
import { ref, reactive, computed, watch, getCurrentInstance, inject, onMounted, onUnmounted } from 'vue';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';
import eventsApis from '../../api';
import EventResourceModal from '../EventResourceModal.vue';
import brandtalkApiCompatibilityImage from '@/assets/images/brandtalk-api-compatibility.webp';

export default {
  name: 'MessageSendDrawer',
  components: {
    EventResourceModal
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    recipients: {
      type: Array,
      default: () => []
    },
    eventId: {
      type: String,
      default: ''
    },
    defaultTemplateCode: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'send'],
  setup(props, { emit }) {
    // 반응형 드로워 너비
    const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200);
    const drawerWidth = computed(() => {
      if (windowWidth.value < 768) {
        return '100%'; // 모바일: 전체 화면
      } else if (windowWidth.value < 1024) {
        return '90%'; // 태블릿: 90%
      }
      return 900; // 데스크톱: 기존 너비
    });

    const handleResize = () => {
      windowWidth.value = window.innerWidth;
    };

    onMounted(() => {
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
    });

    const formRef = ref();
    const sending = ref(false);
    const showRecipientsModal = ref(false);
    const loadingTemplate = ref(false);
    const templateInfo = ref(null);
    const templateError = ref(null);
    const imageLoadError = ref(false);
    const previewImageError = ref(false);
    const excludedHospitals = ref([]);
    const excludedRecipientsCount = ref(0);
    const blacklistFilterEnabled = ref(false);
    const blacklistChecked = ref(false);
    const checkingBlacklist = ref(false);
    const activeMessageTab = ref('alimtalk'); // 기본값: 알림톡

    // 브랜드톡 관련 상태
    const brandtalkForm = reactive({
      messageType: 'U_FI', // U_FI: 이미지형, U_FW: 와이드형 (messageType)
      // upgradeType은 'FY' 고정 (2027년까지 친구톡 호환 방식 사용)
      content: '',
      imageUrl: '',
      buttonName: '',
      buttonLink: '',
      fallbackEnabled: false,
      sendTime: 'now'
    });
    const uploadingImage = ref(false);

    // 발송 버튼 비활성화 조건 (computed)
    const isSendButtonDisabled = computed(() => {
      if (filteredRecipients.value.length === 0) return true;

      if (activeMessageTab.value === 'alimtalk') {
        return !templateInfo.value;
      }

      // 브랜드톡
      if (!brandtalkForm.content) return true;

      // 이미지형/와이드형 모두 이미지 필수
      if (!brandtalkForm.imageUrl) return true;

      return false;
    });

    // 자료실 모달 관련 상태
    const showResourceModal = ref(false);
    
    const blacklistInfo = ref({
      message: '',
      type: 'info',
      details: false,
      totalCount: 0,
      blacklistedCount: 0,
      blacklistedPhoneNumbers: []
    });
    const showBlacklistModal = ref(false);
    
    // API 인스턴스 가져오기
    const instance = getCurrentInstance();
    const apiInstance = instance?.proxy?.$apiInstance;
    
    // eventsApis 초기화
    let api = null;
    if (instance && instance.proxy) {
      api = eventsApis(instance.proxy.$apiInstance, instance.proxy.$store);
    } else {
      console.error('Current instance not available, API initialization failed');
    }
    
    const messageForm = reactive({
      messageType: 'kakao',
      templateCode: '',
      template: '',
      title: '',
      content: '',
      sendTime: 'now',
      scheduledTime: null
    });
    
    const rules = {
      scheduledTime: [
        { required: true, message: '예약 시간을 선택해주세요', trigger: 'change' }
      ]
    };

    // 사용 가능한 병원 목록 (수신자로부터 추출)
    const availableHospitals = computed(() => {
      const hospitals = new Set();
      props.recipients.forEach(recipient => {
        if (recipient.hospital && recipient.hospital !== '-') {
          hospitals.add(recipient.hospital);
        }
      });
      return Array.from(hospitals).sort();
    });
    
    // 필터링된 수신자 목록
    const filteredRecipients = computed(() => {
      let recipients = props.recipients;
      
      // 블랙리스트 필터 적용
      if (blacklistFilterEnabled.value && blacklistChecked.value && blacklistInfo.value.blacklistedPhoneNumbers.length > 0) {
        const blacklistedSet = new Set(blacklistInfo.value.blacklistedPhoneNumbers);
        recipients = recipients.filter(recipient => {
          const normalizedPhone = recipient.phone?.replace(/-/g, '');
          return !blacklistedSet.has(normalizedPhone);
        });
      }
      
      // 병원 필터 적용
      if (excludedHospitals.value.length > 0) {
        recipients = recipients.filter(recipient => {
          // 병원 정보가 없거나 '-'인 경우는 포함
          if (!recipient.hospital || recipient.hospital === '-') {
            return true;
          }
          // 제외 병원 목록에 포함된 경우 제외
          return !excludedHospitals.value.includes(recipient.hospital);
        });
      }
      
      return recipients;
    });
    
    // 병원 필터 변경 핸들러
    const handleHospitalFilterChange = () => {
      // 제외된 수신자 수 계산
      excludedRecipientsCount.value = props.recipients.length - filteredRecipients.value.length;
    };
    
    // 병원 필터 초기화
    const clearHospitalFilter = () => {
      excludedHospitals.value = [];
      excludedRecipientsCount.value = 0;
    };
    
    // 블랙리스트 필터 토글
    const handleBlacklistFilterToggle = (checked) => {
      if (!checked) {
        blacklistChecked.value = false;
        blacklistInfo.value = {
          message: '',
          type: 'info',
          details: false,
          totalCount: 0,
          blacklistedCount: 0,
          blacklistedPhoneNumbers: []
        };
      }
    };
    
    // 블랙리스트 검사
    const checkBlacklist = async () => {
      if (!apiInstance) {
        message.error('API 인스턴스를 찾을 수 없습니다.');
        return;
      }
      
      checkingBlacklist.value = true;
      
      try {
        // 전화번호 추출 및 정규화
        const phoneNumbers = props.recipients.map(r => r.phone?.replace(/-/g, '')).filter(Boolean);
        
        if (phoneNumbers.length === 0) {
          message.warning('검사할 전화번호가 없습니다.');
          return;
        }
        
        const response = await apiInstance.post('/events-v2-blacklist-patients/filter-phone-numbers', {
          phoneNumbers
        });
        
        if (response.data?.success && response.data?.data) {
          const data = response.data.data;
          blacklistChecked.value = true;
          
          blacklistInfo.value = {
            message: data.blacklistedCount === 0 
              ? '블랙리스트에 포함된 번호가 없습니다.'
              : `${data.blacklistedCount}명의 블랙리스트 고객이 발견되었습니다.`,
            type: data.blacklistedCount === 0 ? 'success' : 'warning',
            details: true,
            totalCount: data.totalCount,
            blacklistedCount: data.blacklistedCount,
            blacklistedPhoneNumbers: data.blacklistedPhoneNumbers || []
          };
        }
      } catch (error) {
        console.error('블랙리스트 검사 실패:', error);
        message.error('블랙리스트 검사에 실패했습니다.');
      } finally {
        checkingBlacklist.value = false;
      }
    };
    
    // 블랙리스트 필터 초기화
    const clearBlacklistFilter = () => {
      blacklistFilterEnabled.value = false;
      blacklistChecked.value = false;
      blacklistInfo.value = {
        message: '',
        type: 'info',
        details: false,
        totalCount: 0,
        blacklistedCount: 0,
        blacklistedPhoneNumbers: []
      };
    };
    
    // 템플릿 이미지 URL 가져오기
    const getTemplateImageUrl = () => {
      if (!templateInfo.value) return '';
      
      // templateImageUrl이 있으면 그대로 사용
      if (templateInfo.value.templateImageUrl) {
        // URL이 상대 경로인 경우 전체 URL로 변환
        if (templateInfo.value.templateImageUrl.startsWith('/')) {
          return `${window.location.origin}${templateInfo.value.templateImageUrl}`;
        }
        return templateInfo.value.templateImageUrl;
      }
      
      // templateImageName만 있는 경우 기본 경로와 조합
      if (templateInfo.value.templateImageName) {
        // 예시: /api/templates/images/ 또는 적절한 경로
        return `/api/templates/images/${templateInfo.value.templateImageName}`;
      }
      
      return '';
    };
    
    // 템플릿 코드 조회
    const fetchTemplate = async () => {
      if (!messageForm.templateCode) {
        message.warning('템플릿 코드를 입력해주세요.');
        return;
      }
      
      if (!apiInstance) {
        message.error('API 인스턴스를 찾을 수 없습니다.');
        return;
      }
      
      loadingTemplate.value = true;
      templateError.value = null;
      templateInfo.value = null;
      imageLoadError.value = false;
      previewImageError.value = false;
      
      try {
        const response = await apiInstance.get(`/bizmsg/resell/templates/${messageForm.templateCode}`);
        
        // 중첩된 data 구조 처리
        if (response.data && response.data.data && response.data.data.data) {
          templateInfo.value = response.data.data.data;
          
          message.success('템플릿을 불러왔습니다.');
        } else if (response.data && response.data.data) {
          // 다른 응답 구조 처리 (fallback)
          templateInfo.value = response.data.data;
          message.success('템플릿을 불러왔습니다.');
        }
      } catch (error) {
        console.error('템플릿 조회 실패:', error);
        templateError.value = error.response?.data?.message || '템플릿을 조회할 수 없습니다. 템플릿 코드를 확인해주세요.';
      } finally {
        loadingTemplate.value = false;
      }
    };

    // 드로워가 열릴 때 기본 템플릿 코드로 자동 채우기
    watch(
      () => props.visible,
      (newVisible) => {
        if (newVisible && props.defaultTemplateCode && !messageForm.templateCode) {
          messageForm.templateCode = props.defaultTemplateCode;
          // 자동으로 템플릿 정보 조회
          setTimeout(() => {
            if (messageForm.templateCode) {
              fetchTemplate();
            }
          }, 100);
        }
      }
    );

    // 템플릿 상태 색상
    const getTemplateStatusColor = (status) => {
      const colors = {
        'A': 'green',    // 승인
        'R': 'red',      // 반려
        'S': 'orange',   // 검수중
        'C': 'blue'      // 등록
      };
      return colors[status] || 'default';
    };
    
    // 템플릿 상태 텍스트
    const getTemplateStatusText = (status) => {
      const texts = {
        'A': '승인',
        'R': '반려',
        'S': '검수중',
        'C': '등록'
      };
      return texts[status] || status;
    };
    
    // 메시지 유형 텍스트
    const getMessageTypeText = (type) => {
      const types = {
        'BA': '기본형',
        'EX': '부가정보형',
        'AD': '광고추가형',
        'MI': '복합형'
      };
      return types[type] || type;
    };
    
    
    // 파일을 Base64로 변환하는 함수
    const fileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    };
    
    // 브랜드톡 이미지 업로드 전 처리
    const beforeUploadBrandImage = async (file) => {
      // 파일 타입 체크
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('이미지 파일만 업로드 가능합니다');
        return false;
      }

      // 파일 크기 체크 (2MB - 브랜드톡 제한)
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('이미지 크기는 2MB 이하여야 합니다');
        return false;
      }

      uploadingImage.value = true;

      try {
        if (!api) {
          console.error('API 인스턴스를 찾을 수 없습니다');
          message.error('API 초기화 오류가 발생했습니다.');
          uploadingImage.value = false;
          return false;
        }

        // 브랜드톡 이미지 파일 업로드 API 호출
        api.uploadBrandtalkImageFile(
          file,
          brandtalkForm.messageType === 'U_FW', // 와이드형 여부
          (imageUrl) => {
            brandtalkForm.imageUrl = imageUrl;
            message.success('이미지가 업로드되었습니다');
            uploadingImage.value = false;
          },
          (error) => {
            console.error('브랜드톡 이미지 업로드 실패:', error);
            const errorMessage = error.response?.data?.error || error.message || '브랜드톡 이미지 업로드에 실패했습니다';
            if (errorMessage.includes('API_520')) {
              message.error('파일크기초과: 2MB 미만 사이즈로 재시도해주세요');
            } else {
              message.error(errorMessage);
            }
            uploadingImage.value = false;
          }
        );
      } catch (error) {
        console.error('파일 처리 실패:', error);
        message.error('파일 처리 중 오류가 발생했습니다');
        uploadingImage.value = false;
      }

      return false;
    };

    // 브랜드톡 이미지 제거
    const removeBrandtalkImage = () => {
      brandtalkForm.imageUrl = '';
      message.info('이미지가 제거되었습니다');
    };

    // 이미지 URL 복사
    const copyImageUrl = () => {
      if (brandtalkForm.imageUrl) {
        navigator.clipboard.writeText(brandtalkForm.imageUrl).then(() => {
          message.success('URL이 클립보드에 복사되었습니다');
        }).catch(() => {
          message.error('URL 복사 실패');
        });
      }
    };

    // 자료실에서 파일 선택 핸들러
    const handleResourceFileSelect = (file) => {
      uploadingImage.value = true;
      showResourceModal.value = false; // 모달 먼저 닫기

      // URL 기반 업로드 API 사용 (백엔드가 Azure에서 다운로드 후 업로드)
      api.uploadBrandtalkImage(
        file.url,
        brandtalkForm.messageType === 'U_FW', // 와이드형 여부
        (imageUrl) => {
          // 업로드된 이미지 URL 저장
          brandtalkForm.imageUrl = imageUrl;
          message.success(`이미지가 업로드되었습니다: ${file.name}`);
          uploadingImage.value = false;
        },
        (error) => {
          console.error('브랜드톡 이미지 업로드 실패:', error);
          const errorMessage = error.response?.data?.error || error.message || '이미지 업로드에 실패했습니다';

          // API_520 에러 코드 체크 (파일 크기 초과)
          if (errorMessage.includes('API_520')) {
            message.error('파일크기초과: 2MB 미만 사이즈로 재시도해주세요');
          } else {
            message.error(errorMessage);
          }
          uploadingImage.value = false;
        }
      );
    };
    
    const handleClose = () => {
      // 폼 초기화
      messageForm.messageType = 'kakao';
      messageForm.templateCode = '';
      messageForm.template = '';
      messageForm.title = '';
      messageForm.content = '';
      messageForm.sendTime = 'now';
      messageForm.scheduledTime = null;
      templateInfo.value = null;
      templateError.value = null;

      // 브랜드톡 폼 초기화
      brandtalkForm.messageType = 'U_FI'; // 기본값: 이미지형
      brandtalkForm.content = '';
      brandtalkForm.imageUrl = '';
      brandtalkForm.buttonName = '';
      brandtalkForm.buttonLink = '';
      brandtalkForm.fallbackEnabled = false;
      brandtalkForm.sendTime = 'now';

      excludedHospitals.value = [];
      excludedRecipientsCount.value = 0;
      blacklistFilterEnabled.value = false;
      blacklistChecked.value = false;
      activeMessageTab.value = 'alimtalk'; // 탭 초기화
      showResourceModal.value = false; // 자료실 모달 닫기
      blacklistInfo.value = {
        message: '',
        type: 'info',
        details: false,
        totalCount: 0,
        blacklistedCount: 0,
        blacklistedPhoneNumbers: []
      };
      emit('close');
    };
    
    const handleSend = async () => {
      try {
        // 브랜드톡 탭인 경우
        if (activeMessageTab.value === 'brandtalk') {
          if (filteredRecipients.value.length === 0) {
            message.warning('발송 대상이 없습니다.');
            return;
          }

          if (!brandtalkForm.content) {
            message.warning('메시지 내용을 입력해주세요.');
            return;
          }

          // 이미지형/와이드형 모두 이미지 필수
          if (!brandtalkForm.imageUrl) {
            message.warning('브랜드톡 발송에는 이미지가 필수입니다.');
            return;
          }

          // 야간 시간대 체크 (21:00~08:00)
          const currentHour = new Date().getHours();
          if (currentHour >= 21 || currentHour < 8) {
            message.warning('야간 시간대(21:00~08:00)에는 브랜드톡 발송이 제한됩니다.');
            return;
          }

          sending.value = true;
        } else {
          // 알림톡 탭인 경우
          await formRef.value.validate();

          if (filteredRecipients.value.length === 0) {
            message.warning('발송 대상이 없습니다.');
            return;
          }

          // 템플릿 코드 확인
          if (!messageForm.templateCode) {
            message.warning('템플릿 코드를 입력해주세요.');
            return;
          }

          if (!templateInfo.value) {
            message.warning('템플릿을 먼저 조회해주세요.');
            return;
          }

          sending.value = true;
        }

        try {
          // attendeeIds 추출 (id 또는 _id 사용)
          const attendeeIds = filteredRecipients.value.map(r => r.id || r._id).filter(Boolean);

          if (attendeeIds.length === 0) {
            message.error('참여자 ID를 찾을 수 없습니다.');
            sending.value = false;
            return;
          }

          // 최대 500명 제한 체크
          if (attendeeIds.length > 500) {
            message.warning('한 번에 최대 500명까지만 발송 가능합니다.');
            sending.value = false;
            return;
          }

          // 인증서 수신자 여부 판단 (유입 경로로 판단)
          // deviceId와 referrerId2 모두 없으면 인증서 수신자
          const isCertificateRecipient = filteredRecipients.value.some(r =>
            !r.deviceId && !r.referrerId2
          );

          // API 호출 - 브랜드 메시지와 알림톡 구분
          let requestBody;
          if (activeMessageTab.value === 'brandtalk') {
            // 브랜드 메시지 파라미터 구성 (백엔드 BrandMessageParamDto 스펙에 맞춤)
            // upgradeType: 'FY' = 친구톡 호환 방식 (2027년까지 사용)
            // messageType: 'U_FI' = 이미지형, 'U_FW' = 와이드형
            const brandMessageParam = {
              content: brandtalkForm.content,
              imageUrl: brandtalkForm.imageUrl || null,
              buttonName: brandtalkForm.buttonName || null,
              buttonLink: brandtalkForm.buttonLink || null,
              upgradeType: 'FY', // 친구톡 호환 방식 (2027년까지)
              messageType: brandtalkForm.messageType, // U_FI 또는 U_FW
              fallBackYn: brandtalkForm.fallbackEnabled || false
            };

            requestBody = {
              attendeeIds,
              templateCode: 'BRAND_MESSAGE',
              messageType: 'BRAND_MESSAGE', // BRAND_MESSAGE, ALIM_TALK, NO_MESSAGE 중 하나
              brandMessageParam,
              isCertificateRecipient,
              eventId: props.eventId || undefined
            };
          } else {
            // 알림톡 파라미터 구성
            requestBody = {
              attendeeIds,
              templateCode: messageForm.templateCode,
              isCertificateRecipient,
              eventId: props.eventId || undefined
            };
          }

          const response = await apiInstance.post('/events-v2/message/attendees/send-direct', requestBody);

          if (response.data?.success) {
            const data = response.data.data;
            const messageTypeName = activeMessageTab.value === 'brandtalk' ? '브랜드톡' : '알림톡';
            // totalRecipients가 없으면 filteredRecipients.value.length 사용
            const recipientCount = data?.totalRecipients || filteredRecipients.value.length;
            message.success(
              `${recipientCount}명에게 ${messageTypeName} 발송이 큐에 등록되었습니다.`
            );

            // 발송 이벤트 emit (성공 시)
            emit('send', {
              recipients: filteredRecipients.value,
              message: activeMessageTab.value === 'brandtalk' ? brandtalkForm : messageForm,
              excludedHospitals: excludedHospitals.value,
              response: response.data.data
            });
          } else {
            message.error(response.data?.message || '메시지 발송에 실패했습니다.');
          }
        } catch (error) {
          console.error('메시지 발송 API 호출 실패:', error);
          message.error(
            error.response?.data?.message ||
            '메시지 발송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
          );
        } finally {
          sending.value = false;
        }

      } catch (error) {
        console.error('Validation failed:', error);
      }
    };
    
    // 이미지 로드 에러 처리
    const handleImageError = (event) => {
      console.error('템플릿 이미지 로드 실패:', event.target.src);
      imageLoadError.value = true;
    };
    
    // 미리보기 이미지 로드 에러 처리
    const handlePreviewImageError = (event) => {
      console.error('미리보기 이미지 로드 실패:', event.target.src);
      previewImageError.value = true;
    };
    
    return {
      drawerWidth,
      formRef,
      sending,
      showRecipientsModal,
      loadingTemplate,
      templateInfo,
      templateError,
      imageLoadError,
      previewImageError,
      excludedHospitals,
      excludedRecipientsCount,
      blacklistFilterEnabled,
      blacklistChecked,
      checkingBlacklist,
      blacklistInfo,
      showBlacklistModal,
      activeMessageTab,
      brandtalkForm,
      uploadingImage,
      showResourceModal,
      api,
      isSendButtonDisabled,
      brandtalkApiCompatibilityImage,
      beforeUploadBrandImage,
      removeBrandtalkImage,
      copyImageUrl,
      handleResourceFileSelect,
      availableHospitals,
      filteredRecipients,
      getTemplateImageUrl,
      handleHospitalFilterChange,
      clearHospitalFilter,
      handleBlacklistFilterToggle,
      checkBlacklist,
      clearBlacklistFilter,
      messageForm,
      rules,
      fetchTemplate,
      getTemplateStatusColor,
      getTemplateStatusText,
      getMessageTypeText,
      handleClose,
      handleSend,
      handleImageError,
      handlePreviewImageError
    };
  }
};
</script>

<style scoped>
.message-send-drawer :deep(.ant-drawer-body) {
  padding: 24px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #262626;
}

.recipients-filter-section {
  margin-bottom: 24px;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
}

.filter-controls {
  margin-top: 12px;
}

.blacklist-filter {
  padding-bottom: 16px;
  border-bottom: 1px solid #e8e8e8;
  margin-bottom: 16px;
}

.blacklist-control {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-label {
  font-size: 14px;
  color: #595959;
}

.blacklist-result {
  margin-top: 12px;
}

.blacklist-numbers {
  margin-top: 8px;
}

.blacklist-modal-content .blacklist-numbers-list {
  max-height: 400px;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
}

.excluded-info {
  margin-top: 12px;
}

.recipients-section {
  margin-bottom: 24px;
}

.recipients-info {
  margin-top: 12px;
}

.no-recipients {
  padding: 24px 0;
  text-align: center;
}

.recipients-list {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.recipients-summary {
  margin-top: 12px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
}

.message-section {
  margin-bottom: 24px;
}

.preview-section {
  margin-bottom: 24px;
}

.preview-container {
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  min-height: 120px;
}

/* SMS 미리보기 스타일 */
.phone-preview {
  max-width: 300px;
  margin: 0 auto;
}

.sms-bubble {
  background: white;
  padding: 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* 카카오톡 미리보기 스타일 */
.kakao-preview {
  max-width: 360px;
  margin: 0 auto;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.kakao-header {
  background: #fee500;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
}

.kakao-title {
  padding: 12px 12px 8px;
  font-weight: 600;
  font-size: 15px;
  color: #000;
}

.kakao-content {
  padding: 0 12px 12px;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  white-space: pre-wrap;
}

/* 카카오톡 이미지 스타일 */
.kakao-image {
  position: relative;
  width: 100%;
}

.kakao-image img {
  width: 100%;
  height: auto;
  display: block;
}

/* 카카오톡 버튼 스타일 */
.kakao-buttons {
  border-top: 1px solid #e5e5e5;
}

.kakao-button {
  padding: 12px;
  text-align: center;
  color: #3b88c3;
  font-size: 14px;
  font-weight: 500;
  border-bottom: 1px solid #e5e5e5;
  cursor: pointer;
}

.kakao-button:last-child {
  border-bottom: none;
}

.kakao-button:hover {
  background: #f9f9f9;
}


.yellow-button {
  background-color: #FFD814 !important;
  border-color: #FFD814 !important;
}

.yellow-button:hover {
  background-color: #E5C112 !important;
  border-color: #E5C112 !important;
}

/* 템플릿 정보 스타일 */
.template-info {
  margin-top: 12px;
}

.mt-2 {
  margin-top: 8px;
}

.template-info .ant-descriptions {
  background: #f5f5f5;
  border-radius: 4px;
}

.template-info .ant-descriptions-item-label {
  background: #fafafa;
  font-weight: 500;
}


/* 템플릿 이미지 미리보기 */
.template-image-preview {
  margin-bottom: 12px;
  padding: 12px;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  text-align: center;
}

.template-image-preview img {
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
}

/* 템플릿 버튼 정보 */
.template-button-info {
  margin-bottom: 4px;
}

.template-button-info:last-child {
  margin-bottom: 0;
}

/* 브랜드톡 콘텐츠 스타일 */
.brandtalk-content {
  padding: 8px 0;
}

.brandtalk-type-section {
  margin-bottom: 16px;
}

/* 탭 스타일 */
.message-send-drawer :deep(.ant-tabs) {
  margin-bottom: 0;
}

.message-send-drawer :deep(.ant-tabs-nav) {
  margin-bottom: 24px;
}

.message-send-drawer :deep(.ant-tabs-tab) {
  padding: 12px 20px;
  font-size: 15px;
}

.message-send-drawer :deep(.ant-tabs-content) {
  padding: 0;
}

/* 모바일 반응형 스타일 */
@media (max-width: 767px) {
  .message-send-drawer :deep(.ant-drawer-content-wrapper) {
    max-width: 100vw !important;
  }

  .message-send-drawer :deep(.ant-drawer-header) {
    padding: 12px 16px;
  }

  .message-send-drawer :deep(.ant-drawer-body) {
    padding: 12px;
  }

  .section-title {
    font-size: 14px !important;
  }

  .recipients-filter-section {
    padding: 12px !important;
  }

  .blacklist-control {
    flex-wrap: wrap;
    gap: 8px !important;
  }

  .filter-label {
    width: 100%;
  }

  .recipients-list {
    max-height: 120px;
    overflow-y: auto;
  }

  .preview-container {
    padding: 12px !important;
  }

  .kakao-preview {
    max-width: 100% !important;
  }

  .template-info :deep(.ant-descriptions-item-label) {
    width: 80px !important;
    font-size: 12px !important;
  }

  .template-info :deep(.ant-descriptions-item-content) {
    font-size: 12px !important;
  }

  .message-send-drawer :deep(.ant-tabs-tab) {
    padding: 8px 12px !important;
    font-size: 13px !important;
  }

  .message-send-drawer :deep(.ant-drawer-footer) {
    padding: 12px 16px !important;
  }

  .message-send-drawer :deep(.ant-drawer-footer .ant-space) {
    width: 100%;
    flex-direction: column;
  }

  .message-send-drawer :deep(.ant-drawer-footer .ant-btn) {
    width: 100%;
  }
}

/* 태블릿 반응형 스타일 */
@media (min-width: 768px) and (max-width: 1023px) {
  .message-send-drawer :deep(.ant-drawer-body) {
    padding: 16px;
  }

  .recipients-filter-section {
    padding: 14px !important;
  }

  .kakao-preview {
    max-width: 320px !important;
  }
}
</style>

<!-- 브랜드톡 툴팁 전역 스타일 (body에 렌더링되므로 scoped 불가) -->
<style>
.brandtalk-tooltip {
  max-width: 800px !important;
}

.brandtalk-tooltip .ant-tooltip-inner {
  max-width: 800px !important;
  width: 800px;
  padding: 16px 20px;
  font-size: 13px;
  line-height: 1.7;
  background-color: #ffffff !important;
  color: #333333 !important;
  box-shadow: 0 3px 6px -4px rgba(0,0,0,0.12), 0 6px 16px 0 rgba(0,0,0,0.08), 0 9px 28px 8px rgba(0,0,0,0.05);
}

.brandtalk-tooltip .ant-tooltip-arrow::before {
  background-color: #ffffff !important;
}

.brandtalk-tooltip .ant-tooltip-arrow-content {
  --antd-arrow-background-color: #ffffff;
}
</style>